import React, { useEffect, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { launchesCategory } from '../shared/jsonData';
import UseFetchPost from '../shared/useFetchPost';
import { cn } from '../shared/utils';

import 'swiper/scss';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';
import './RaffleSlider.scss';
//import Button from '../Button';

// Parses a date string in the format 'DD-MMM-YYYY HH:mm:ss' to a Date object
function parseDate(s) {
  if (s === undefined) return;
  var months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11
  };
  var p = s.split(' ');
  var d = p[0].split('-');
  var t = p[1].split(':');

  return new Date(d[2], months[d[1].toLowerCase()], d[0], t[0], t[1], t[2]);
}

// Returns current window dimensions (width, height)
function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

// Custom hook to track window dimensions
function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

// Utility function to calculate time left for a launch
const calculateTimeLeft = (startdate, enddate) => {
  const now = new Date();
  const start = new Date(startdate);
  const end = new Date(enddate);

  let timeLeft = {};

  if (now < start) {
    // Launch hasn't started yet
    const difference = start - now;
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      message: 'Starts in'
    };
  } else if (now >= start && now < end) {
    // Launch is live
    const difference = end - now;
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      message: 'Draw closes in'
    };
  } else {
    // Launch has ended
    timeLeft = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      message: 'Draw Closed'
    };
  }

  return timeLeft;
};

// Sorts launches: live launches first (soonest ending), then recently expired
const sortLaunches = (prods, data) => {
  const currentDate = new Date();

  // Ensure all products have an enddate
  prods.forEach((item) => {
    if (item.enddate === undefined) item.enddate = '01-Jan-1970 00:00:00';
  });

  const liveProducts = prods.filter(
    (product) => parseDate(product.enddate) > currentDate
  );
  const expiredProducts = prods.filter(
    (product) => parseDate(product.enddate) <= currentDate
  );

  // Sort live products by enddate ascending (soonest ending first)
  liveProducts.sort((a, b) => parseDate(a.enddate) - parseDate(b.enddate));

  // Sort live products by enddate descending (furthest ending first)
  // liveProducts.sort((a, b) => parseDate(b.enddate) - parseDate(a.enddate));

  if (data) {
    const skus = data && data.split(',').map((sku) => sku.trim());
    liveProducts.forEach((i) => {
      // check if the product SKU is in the skus list and push it to the front if it is
      if (skus.includes(i.productid)) {
        const index = liveProducts.indexOf(i);
        console.log('Found SKU:', i.productid, index);
        if (index > -1) {
          liveProducts.splice(index, 1); // Remove from current position
          liveProducts.unshift(i); // Add to the front
        }
      }
    });
  }

  // Sort expired products by enddate descending (most recently expired first)
  expiredProducts.sort((a, b) => parseDate(b.enddate) - parseDate(a.enddate));

  // If 3 live, show all 3; if more, show up to 6; if less, fill with expired
  const combinedProducts =
    liveProducts.length === 3
      ? liveProducts // if 3, show all 3
      : liveProducts.length > 3
      ? liveProducts.slice(0, 6) // if more, show all (limit to 6)
      : [...liveProducts, ...expiredProducts.slice(0, 3 - liveProducts.length)]; // less than 3, show recently ended to fill (3 max)

  return combinedProducts;
};

// RaffleSlider main component
const RaffleSlider = ({ data }) => {
  // State for fetched launches
  const [nqdata, setHomepageData] = useState({ cards: [] });
  // Track which slide to start on
  const [initialSlide, setInitialSlide] = useState(0);
  // Ref to store swiper instance
  const swiperRef = useRef(null);
  // Fetch launches data
  const getNQLaunches = UseFetchPost(
    launchesCategory.endPointUrl,
    launchesCategory.endPointPrams
  );

  useEffect(() => {
    // When data is fetched, process and sort launches
    if (getNQLaunches && getNQLaunches.data && getNQLaunches.data.products) {
      const content = getNQLaunches.data.products.products;

      // Map API data to expected format for slider
      const testList = content.map((item) => {
        const product = item.product;
        const startdate = product.option.startdate;
        const enddate =
          item.product.attributes.launch_end_date === undefined
            ? undefined
            : item.product.attributes.launch_end_date[0];

        return { ...product, startdate, enddate };
      });
      const sorted = sortLaunches(testList, data);
      setHomepageData({ cards: sorted });

      // Find the index of the live product with the soonest ending (if any)
      // const now = new Date();
      // let soonestIndex = 0;
      // let soonestTime = Infinity;
      // sorted.forEach((item, idx) => {
      //   const end = parseDate(item.enddate);
      //   if (end > now && end - now < soonestTime) {
      //     soonestTime = end - now;
      //     soonestIndex = idx;
      //   }
      // });

      setInitialSlide(0);
    }
  }, [getNQLaunches, data]);

  // Effect to ensure Swiper moves to correct slide after data is loaded
  useEffect(() => {
    if (
      swiperRef.current &&
      nqdata.cards &&
      Array.isArray(nqdata.cards) &&
      nqdata.cards.length > 0
    ) {
      console.log('Swiper: slideToLoop called', {
        initialSlide,
        cards: nqdata.cards.map((c) => c.name || c.id),
        swiper: swiperRef.current
      });
      swiperRef.current.slideToLoop(initialSlide, 0);
      console.log(
        'Swiper: activeIndex after slideToLoop:',
        swiperRef.current.activeIndex
      );
    }
  }, [nqdata.cards, initialSlide]);

  const { width } = useWindowDimensions();

  return (
    <section className={'container pb-12'}>
      <div className="flex items-center justify-between w-full mb-6">
        <h3 className="launches-title">Launches</h3>
        <a
          href="/launches"
          className="px-5 py-3 tracking-wider text-base border border-solid border-[#dedede]"
        >
          View All{' '}
        </a>
      </div>
      <Swiper
        modules={[Navigation, Pagination]}
        navigation={width >= 768 && nqdata.cards.length > 3}
        pagination={{ clickable: true }}
        loop={width < 425 || nqdata.cards.length > 3}
        slidesPerView={1.2}
        spaceBetween={10}
        breakpoints={{
          768: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            spaceBetween: 16
          }
        }}
        className="-mr-6 pr-4 md:mr-0 md:pr-0"
        initialSlide={initialSlide}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {/* Render each launch as a Swiper slide */}
        {nqdata.cards &&
          Array.isArray(nqdata.cards) &&
          nqdata.cards.map((slide, index) => (
            <SwiperSlide key={index}>
              <LaunchProduct slide={slide} />
            </SwiperSlide>
          ))}
      </Swiper>
    </section>
  );
};

// Individual launch product card
const LaunchProduct = ({ slide }) => {
  // State for countdown timer
  const [timeLeft, setTimeLeft] = useState(
    calculateTimeLeft(slide.startdate, slide.enddate)
  );

  useEffect(() => {
    // Update countdown every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(slide.startdate, slide.enddate));
    }, 1000);

    return () => clearInterval(timer);
  }, [slide.startdate, slide.enddate]);

  return (
    <a className={'launch-card flex flex-col p-0 w-full mb-0'} href={slide.url}>
      <div className="relative h-[192px] overflow-hidden lg:h-[20vw] bg-[#e6e6e6]">
        <img
          className={cn(
            '!absolute -bottom-20 left-1/2 -translate-x-1/2',
            timeLeft.message === 'Draw Closed' && 'opacity-30'
          )}
          src={slide.image}
          alt={slide.name}
        />
      </div>
      {/* Display the countdown timer */}
      <CountdownTimer timeLeft={timeLeft} />
    </a>
  );
};

// Countdown Timer display for a launch
const CountdownTimer = ({ timeLeft }) => {
  return (
    <div className="absolute bottom-0 left-0">
      {timeLeft.message && (
        <p
          className={cn(
            '!text-xs !tracking-wider p-2 bg-white border border-[#e6e6e6] border-solid',
            timeLeft.message === 'Draw Closed' && 'font-bold'
          )}
        >
          {timeLeft.message}{' '}
          {timeLeft.message !== 'Draw Closed' && (
            <span className="font-bold">
              {`${timeLeft.days}d ${timeLeft.hours}h ${timeLeft.minutes}m`}
            </span>
          )}
        </p>
      )}
    </div>
  );
};

export default RaffleSlider;
